import React, { memo, useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Skeleton,
  Box,
  Tooltip,
  TableSortLabel,
  useTheme,
  alpha,
  Typography,
  Stack,
  Checkbox,
  Chip,
  Collapse,
  Card,
  CardContent,
  Divider,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Visibility as ViewIcon,
  ContentPaste as ContentPasteIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Medication as MedicationIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { motion } from 'framer-motion';
import type {
  MedicalReservationRecord,
  MedicalReservationFilterOptions,
} from '../../types/medicalReservation';
import { BUSINESS_STATUS_CONFIG } from '../../types/medicalReservation';
import { getNotificationReasons } from '../../utils/notificationChecker';

interface ReservationTableProps {
  data: MedicalReservationRecord[];
  isLoading: boolean;
  onViewDetails: (record: MedicalReservationRecord) => void;
  onEdit: (record: MedicalReservationRecord) => void;
  onAssign?: (record: MedicalReservationRecord) => void;
  onDelete?: (record: MedicalReservationRecord) => void;
  onToggleNotificationStatus?: (record: MedicalReservationRecord) => void;
  filters: MedicalReservationFilterOptions;
  onFiltersChange: (filters: MedicalReservationFilterOptions) => void;
  meta?: {
    current_page: number;
    total_pages: number;
    total_records: number;
    page_size: number;
  };
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  compactMode?: boolean;
}

/**
 * 診療予約レコードテーブルコンポーネント
 * 
 * 意図：
 * - 個別レコードの一覧表示
 * - 担当医師、患者名、ステータス等の主要情報表示
 * - 詳細表示・編集アクションの提供
 * 
 * 注意点：
 * - 大量データ対応（仮想スクロール検討）
 * - レスポンシブ対応
 * - アクセシビリティ対応
 * 
 * 改善提案：
 * - react-windowによる仮想スクロール
 * - カラムの表示/非表示切り替え
 * - CSVエクスポート機能
 */
export const ReservationTable = memo<ReservationTableProps>(({
  data,
  isLoading,
  onViewDetails,
  onEdit,
  onAssign,
  onDelete,
  onToggleNotificationStatus,
  filters,
  onFiltersChange,
  meta,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  compactMode = false,
}) => {
  const theme = useTheme();
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // 行の展開/折りたたみ処理
  const handleToggleExpand = (recordId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordId)) {
        newSet.delete(recordId);
      } else {
        newSet.add(recordId);
      }
      return newSet;
    });
  };

  // ソート処理
  const handleSort = (property: keyof MedicalReservationRecord) => {
    const isAsc = filters.sort_by === property && filters.sort_order === 'asc';
    onFiltersChange({
      ...filters,
      sort_by: property,
      sort_order: isAsc ? 'desc' : 'asc',
    });
  };

  // ページネーション処理
  const handleChangePage = (event: unknown, newPage: number) => {
    onFiltersChange({
      ...filters,
      page: newPage + 1,
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      page_size: parseInt(event.target.value, 10),
      page: 1,
    });
  };

  // レコードを無効化すべきかどうか判定
  // 過去の診療時間 OR 業務ステータスが「完了」の場合にtrue
  const shouldDisableRecord = (record: MedicalReservationRecord): boolean => {
    try {
      // 診療開始時間が過去かチェック
      const consultationDate = new Date(record.consultationStartTime);
      const now = new Date();
      const isPast = consultationDate < now;

      // 業務ステータスが「完了」かチェック
      const isBusinessCompleted = record.businessStatus === 'completed';

      // 以下のいずれかの条件を満たす場合に true を返す：
      // 1. 過去の診療時間
      // 2. 業務ステータスが「完了」
      return isPast || isBusinessCompleted;
    } catch {
      return false;
    }
  };

  // 選択可能なデータ数を計算（グレーアウトされていないデータ）
  const selectableData = useMemo(() =>
    data.filter(record => !shouldDisableRecord(record)),
    [data]
  );
  const selectableCount = selectableData.length;

  // 全選択/全解除の判定（選択可能なデータを基準にする）
  const isAllSelected = selectableCount > 0 && selectedIds.length === selectableCount;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < selectableCount;

  // 選択ヘッダーの処理
  const handleSelectAllClick = () => {
    if (isAllSelected) {
      onClearSelection();
    } else {
      onSelectAll();
    }
  };

  // 日時フォーマット
  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), compactMode ? 'MM/dd HH:mm' : 'yyyy/MM/dd HH:mm', { locale: ja });
    } catch {
      return dateString;
    }
  };

  // テーブルヘッダー定義
  const columns = [
    { id: 'expand', label: '', sortable: false, width: 50 },
    { id: 'select', label: '', sortable: false, width: 50 },
    { id: 'doctorInCharge', label: '担当医師', sortable: true, width: 120 },
    { id: 'anybotId', label: 'anybot ID', sortable: true, width: 110 },
    { id: 'patientName', label: '患者名', sortable: true, width: 120 },
    { id: 'consultationStartTime', label: '診療開始時間', sortable: true, width: 160 },
    { id: 'notificationStatus', label: '通知ステータス', sortable: true, width: 140 },
    { id: 'listUpdateCompleted', label: '診療リスト貼り付け', sortable: false, width: 120 },
    { id: 'actions', label: 'アクション', sortable: false, width: onAssign ? 160 : 120 },
    { id: 'businessStatus', label: '業務ステータス', sortable: false, width: 120 },
  ];

  if (isLoading) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id} style={{ width: column.width }}>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column.id}>
                      <Skeleton variant="text" width="90%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <TableContainer sx={{ maxHeight: compactMode ? 'calc(100vh - 320px)' : 'calc(100vh - 400px)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ width: column.width }}
                  sx={{
                    fontWeight: 600,
                    bgcolor: theme.palette.grey[50],
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    py: compactMode ? 1 : 2,
                  }}
                >
                  {column.id === 'expand' ? (
                    null
                  ) : column.id === 'select' ? (
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAllClick}
                      size="small"
                      disabled={selectableCount === 0}
                    />
                  ) : column.sortable ? (
                    <TableSortLabel
                      active={filters.sort_by === column.id}
                      direction={filters.sort_by === column.id ? filters.sort_order : 'asc'}
                      onClick={() => handleSort(column.id as keyof MedicalReservationRecord)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                  <Box>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      レコードがありません
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CSVファイルをアップロードしてデータを作成してください
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((record) => {
                const isDisabled = shouldDisableRecord(record);
                return (
                  <React.Fragment key={record.id}>
                    <TableRow
                      component={motion.tr}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      onMouseEnter={() => !isDisabled && setHoveredRow(record.id)}
                      onMouseLeave={() => !isDisabled && setHoveredRow(null)}
                      sx={{
                        backgroundColor: isDisabled
                          ? theme.palette.grey[100]
                          : hoveredRow === record.id
                          ? alpha(theme.palette.primary.main, 0.04)
                          : expandedRows.has(record.id)
                          ? alpha(theme.palette.primary.main, 0.02)
                          : 'transparent',
                        opacity: isDisabled ? 0.6 : 1,
                        '&:hover': {
                          backgroundColor: isDisabled
                            ? theme.palette.grey[100]
                            : alpha(theme.palette.primary.main, 0.04),
                        },
                        '& td': {
                          color: isDisabled ? theme.palette.text.disabled : 'inherit',
                        },
                      }}
                    >
                    <TableCell sx={{ py: compactMode ? 1 : 2 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleExpand(record.id)}
                        sx={{
                          transition: 'transform 0.3s',
                          transform: expandedRows.has(record.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ py: compactMode ? 1 : 2 }}>
                      <Checkbox
                        checked={selectedIds.includes(record.id)}
                        onChange={() => onToggleSelect(record.id)}
                        size="small"
                        disabled={isDisabled}
                      />
                    </TableCell>
                  <TableCell sx={{ py: compactMode ? 1 : 2 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {record.doctorInCharge}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: compactMode ? 1 : 2 }}>
                    <Typography variant="body2" fontFamily="monospace" color="primary.main">
                      {record.anybotId}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: compactMode ? 1 : 2 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {record.patientName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: compactMode ? 1 : 2 }}>
                    <Typography variant="body2">
                      {formatDateTime(record.consultationStartTime)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: compactMode ? 1 : 2, textAlign: 'center' }}>
                    <Checkbox
                      checked={record.actionStatus !== 'notification_required'}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (onToggleNotificationStatus && !isDisabled) {
                          onToggleNotificationStatus(record);
                        }
                      }}
                      disabled={isDisabled}
                      size="small"
                      sx={{
                        color: record.actionStatus !== 'notification_required'
                          ? theme.palette.success.main
                          : theme.palette.action.disabled,
                        transition: 'all 0.2s ease',
                        '&:not(.Mui-disabled):hover': {
                          transform: 'scale(1.1)',
                        },
                        '&.Mui-disabled': {
                          color: record.actionStatus !== 'notification_required'
                            ? theme.palette.success.main
                            : theme.palette.action.disabled,
                        },
                      }}
                    />
                    {record.actionStatus === 'notification_required' && (
                      <Tooltip title={getNotificationReasons(record).join(', ')} placement="top">
                        <WarningIcon
                          sx={{
                            fontSize: 16,
                            color: theme.palette.warning.main,
                            verticalAlign: 'middle',
                            ml: 0.5,
                            cursor: 'help'
                          }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: compactMode ? 1 : 2, textAlign: 'center' }}>
                    <Checkbox
                      checked={record.paste_flag || false}
                      disabled
                      size="small"
                      sx={{
                        '&.Mui-disabled': {
                          color: record.paste_flag ? theme.palette.success.main : theme.palette.action.disabled,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: compactMode ? 1 : 2 }}>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="データ詳細一覧" placement="top">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onViewDetails(record)}
                            disabled={isDisabled}
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                              },
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title="貼り付け"
                        placement="top"
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(record);
                            }}
                            disabled={isDisabled}
                            sx={{
                              transition: 'all 0.2s ease',
                              opacity: isDisabled ? 0.5 : 1,
                              '&:hover:not(:disabled)': {
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                color: theme.palette.warning.main,
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            <ContentPasteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {onDelete && (
                        <Tooltip title="削除" placement="top">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onDelete(record)}
                              disabled={isDisabled}
                              sx={{
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  color: theme.palette.error.main,
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ py: compactMode ? 1 : 2 }}>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={record.businessStatus || 'uncompleted'}
                        disabled
                        size="small"
                        sx={{
                          fontSize: '0.875rem',
                          backgroundColor: BUSINESS_STATUS_CONFIG[record.businessStatus || 'uncompleted'].backgroundColor,
                          color: BUSINESS_STATUS_CONFIG[record.businessStatus || 'uncompleted'].textColor,
                          '& .MuiSelect-select': {
                            py: 0.5,
                            px: 1,
                          },
                        }}
                      >
                        {Object.entries(BUSINESS_STATUS_CONFIG).map(([key, config]) => (
                          <MenuItem key={key} value={key}>
                            {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
                
                {/* アコーディオン展開部分 */}
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expandedRows.has(record.id)} timeout="auto" unmountOnExit>
                      <Box sx={{ m: 2 }}>
                        <Grid container spacing={3}>
                          {/* 基本情報 */}
                          <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                  <PersonIcon color="primary" />
                                  <Typography variant="h6" fontWeight={600}>
                                    基本情報
                                  </Typography>
                                </Stack>
                                <Divider sx={{ mb: 2 }} />
                                <Stack spacing={1.5}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">患者名:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.patientName}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">anybot ID:</Typography>
                                    <Typography variant="body2" fontWeight={500} fontFamily="monospace">
                                      {record.anybotId}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">会員登録:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.csvData?.['会員登録'] || '未登録'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">性別:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.csvData?.['性別'] || '未設定'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">生年月日:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.csvData?.['生年月日'] || '未設定'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* 診療情報 */}
                          <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                  <HospitalIcon color="primary" />
                                  <Typography variant="h6" fontWeight={600}>
                                    診療情報
                                  </Typography>
                                </Stack>
                                <Divider sx={{ mb: 2 }} />
                                <Stack spacing={1.5}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">診療開始時間:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {formatDateTime(record.consultationStartTime)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">担当医師:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.doctorInCharge}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">診療科:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.csvData?.['診療科'] || '一般内科'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">症状:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.csvData?.['症状'] || '記載なし'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">カウンセラー対応:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.csvData?.['カウンセラー対応'] || 'なし'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* 薬剤・保険情報 */}
                          <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                  <MedicationIcon color="primary" />
                                  <Typography variant="h6" fontWeight={600}>
                                    薬剤・保険情報
                                  </Typography>
                                </Stack>
                                <Divider sx={{ mb: 2 }} />
                                <Stack spacing={1.5}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">処方薬:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.csvData?.['処方薬'] || '処方なし'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">薬剤アレルギー:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.csvData?.['薬剤アレルギー'] || 'なし'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">保険種別:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.csvData?.['保険種別'] || '社保'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">保険証番号:</Typography>
                                    <Typography variant="body2" fontWeight={500} fontFamily="monospace">
                                      {record.csvData?.['保険証番号'] || '未登録'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* ステータス・その他 */}
                          <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                  <AssignmentIcon color="primary" />
                                  <Typography variant="h6" fontWeight={600}>
                                    ステータス・その他
                                  </Typography>
                                </Stack>
                                <Divider sx={{ mb: 2 }} />
                                <Stack spacing={1.5}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">ステータス:</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Checkbox
                                        checked={record.actionStatus !== 'notification_required'}
                                        disabled
                                        size="small"
                                        sx={{
                                          padding: 0,
                                          '&.Mui-disabled': {
                                            color: record.actionStatus !== 'notification_required'
                                              ? theme.palette.success.main
                                              : theme.palette.action.disabled,
                                          },
                                        }}
                                      />
                                      {record.actionStatus === 'notification_required' && (
                                        <Tooltip title={getNotificationReasons(record).join(', ')} placement="top">
                                          <WarningIcon
                                            sx={{
                                              fontSize: 16,
                                              color: theme.palette.warning.main,
                                              ml: 0.5,
                                              cursor: 'help'
                                            }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">診療リスト貼り付け:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {record.paste_flag ? '完了' : '未完了'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">タグ:</Typography>
                                    <Stack direction="row" spacing={0.5}>
                                      {record.csvData?.['tags']?.split(',').filter(Boolean).map((tag: string, index: number) => (
                                        <Chip key={index} label={tag.trim()} size="small" />
                                      )) || <Typography variant="body2">なし</Typography>}
                                    </Stack>
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      メモ:
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                      bgcolor: theme.palette.grey[50],
                                      p: 1,
                                      borderRadius: 1,
                                      minHeight: 40,
                                    }}>
                                      {record.notes || '記載なし'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              )})
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {meta && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={meta.total_records}
          rowsPerPage={meta.page_size}
          page={meta.current_page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count !== -1 ? count : `${to}以上`}`
          }
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        />
      )}
    </Paper>
  );
});